import {
  CashCheckVerificationMethod,
  ICashCheckLine,
  isWeightVerificationMode,
} from "../../models/order/cash-check";

const normalizeBarcode = (barcode: unknown): string =>
  typeof barcode === "string" ? barcode.trim() : "";

const lineHasBarcode = (line: ICashCheckLine, barcode: string): boolean =>
  Array.isArray(line.barcodes) &&
  line.barcodes.some((candidate) => normalizeBarcode(candidate) === barcode);

const isLineEligibleForScan = (line: ICashCheckLine): boolean => {
  if (!isWeightVerificationMode(line.verificationMode)) return !line.isComplete;

  return !line.verifications.some(
    (verification) =>
      verification.method === CashCheckVerificationMethod.MANUAL_WEIGHT,
  );
};

export interface ICashCheckScanCandidates {
  matchedProductOrderIds: number[];
  eligibleProductOrderIds: number[];
  allMatchesAreCompletedNonWeighted: boolean;
}

/**
 * Resolves only against the current order snapshot. Weighted lines remain
 * eligible after satisfying their minimum; completed non-weighted lines do not.
 * The server is still authoritative when the chosen scan is submitted.
 */
export const resolveCashCheckScanCandidates = (
  lines: ICashCheckLine[],
  scannedBarcode: string,
  parsedProductCode?: string,
): ICashCheckScanCandidates => {
  const barcode = normalizeBarcode(scannedBarcode);
  const productCode = normalizeBarcode(parsedProductCode);
  const matchedLines = lines.filter((line) => {
    const weighted = isWeightVerificationMode(line.verificationMode);
    const directMatch = !weighted && lineHasBarcode(line, barcode);
    const weightedProductCodeMatch =
      Boolean(productCode) && weighted && lineHasBarcode(line, productCode);

    return directMatch || weightedProductCodeMatch;
  });
  const eligibleLines = matchedLines.filter(isLineEligibleForScan);

  return {
    matchedProductOrderIds: matchedLines.map((line) => line.productOrderId),
    eligibleProductOrderIds: eligibleLines.map((line) => line.productOrderId),
    allMatchesAreCompletedNonWeighted:
      matchedLines.length > 0 &&
      matchedLines.every(
        (line) =>
          !isWeightVerificationMode(line.verificationMode) && line.isComplete,
      ),
  };
};

export const filterEligibleCashCheckCandidateIds = (
  lines: ICashCheckLine[],
  candidateProductOrderIds: number[],
): ICashCheckScanCandidates => {
  const candidateIds = new Set(candidateProductOrderIds);
  const candidates = lines.filter((line) =>
    candidateIds.has(line.productOrderId),
  );
  const eligibleLines = candidates.filter(isLineEligibleForScan);

  return {
    matchedProductOrderIds: candidates.map((line) => line.productOrderId),
    eligibleProductOrderIds: eligibleLines.map((line) => line.productOrderId),
    allMatchesAreCompletedNonWeighted:
      candidates.length > 0 &&
      candidates.every(
        (line) =>
          !isWeightVerificationMode(line.verificationMode) && line.isComplete,
      ),
  };
};
